import type {
  AppError,
  ByteSpan,
  Demand,
  DownloadMode,
  DownloadState,
  MediaKind,
  SessionSnapshot,
} from '../core/types';
import { makeError, OneFlowError } from '../core/errors';
import { IntervalSet } from '../core/ranges';
import { SNIFF_BYTES, resolveKind, sniffBytes } from '../core/sniff';
import {
  kindFromExtension,
  kindFromMime,
  parseSourceUrl,
  safeFilename,
  urlFingerprint,
  type ParsedSource,
} from '../core/url';
import { StorageRpc } from '../workers/rpc';
import type { DownloadCommand, DownloadEvent } from '../workers/protocol';
import { defaultConcurrency, detectCapabilities, type BrowserCapabilities } from './capabilities';
import {
  deleteSessionRecord,
  loadSessions,
  patchSession,
  saveSession,
  type SessionRecord,
} from './db';
import { revokeAllFor } from './object-urls';
import { SessionHandle } from './session-handle';

export interface EngineSettings {
  mode: DownloadMode;
  rememberSignedLinks: boolean;
}

export type EngineEvent =
  | { type: 'record'; record: SessionRecord }
  | { type: 'snapshot'; id: string; snapshot: SessionSnapshot }
  | { type: 'removed'; id: string }
  | { type: 'ready' }
  | { type: 'worker-crashed' };

/** Raised when a session can only continue after the user pastes its link again. */
export class NeedsUrl extends Error {
  constructor(readonly id: string) {
    super('link needed');
  }
}

let epochSeed = Date.now();
const nextEpoch = () => ++epochSeed;

/**
 * Owns the two workers and the session lifecycle. Components talk to this,
 * never to workers directly. ARCH-018, ARCH-019
 */
export class Engine {
  readonly caps: BrowserCapabilities = detectCapabilities();
  private storageWorker: Worker | null = null;
  private downloadWorker: Worker | null = null;
  private rpc: StorageRpc | null = null;
  private starting: Promise<void> | null = null;
  private readonly records = new Map<string, SessionRecord>();
  private readonly handles = new Map<string, SessionHandle>();
  private readonly epochs = new Map<string, number>();
  private readonly listeners = new Set<(e: EngineEvent) => void>();
  private readonly lastPersist = new Map<string, { at: number; state: DownloadState }>();
  private readonly sniffed = new Set<string>();
  /** Links held in memory for this page session only. Never persisted, so a signed link cannot leak to disk. */
  private readonly liveUrls = new Map<string, string>();

  constructor(private readonly getSettings: () => EngineSettings) {}

  onEvent(fn: (e: EngineEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: EngineEvent): void {
    for (const l of [...this.listeners]) l(e);
  }

  // ───────────────── workers ─────────────────

  ensureStarted(): Promise<void> {
    this.starting ??= this.startWorkers().catch((e) => {
      this.starting = null;
      throw e;
    });
    return this.starting;
  }

  private async startWorkers(): Promise<void> {
    if (!this.caps.opfs || !this.caps.workers) throw new OneFlowError('STORAGE_UNAVAILABLE');
    const storage = new Worker(new URL('../workers/storage.worker.ts', import.meta.url), {
      type: 'module',
    });
    const download = new Worker(new URL('../workers/download.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.storageWorker = storage;
    this.downloadWorker = download;

    const storageReady = new Promise<void>((resolve) => {
      const h = (e: MessageEvent) => {
        if ((e.data as { type?: string } | undefined)?.type === 'ready') {
          storage.removeEventListener('message', h);
          resolve();
        }
      };
      storage.addEventListener('message', h);
    });
    const downloadReady = new Promise<void>((resolve) => {
      const h = (e: MessageEvent<DownloadEvent>) => {
        if (e.data.type === 'ready') {
          download.removeEventListener('message', h);
          resolve();
        }
      };
      download.addEventListener('message', h);
    });

    this.rpc = new StorageRpc(storage);
    const channel = new MessageChannel();
    storage.postMessage({ type: 'attach', port: channel.port1 }, [channel.port1]);
    download.postMessage({ type: 'storagePort', port: channel.port2 }, [channel.port2]);

    download.addEventListener('message', (e: MessageEvent<DownloadEvent>) => {
      if (e.data.type === 'snapshot') this.onSnapshot(e.data.snapshot);
    });
    const crashed = () => this.onWorkerCrash();
    storage.addEventListener('error', crashed);
    download.addEventListener('error', crashed);
    storage.addEventListener('messageerror', crashed);
    download.addEventListener('messageerror', crashed);

    await Promise.all([storageReady, downloadReady]);
  }

  private onWorkerCrash(): void {
    this.rpc?.failAll(new DOMException('storage worker stopped', 'InvalidStateError'));
    this.storageWorker?.terminate();
    this.downloadWorker?.terminate();
    this.storageWorker = this.downloadWorker = this.rpc = null;
    this.starting = null;
    for (const r of this.records.values()) {
      if (r.state === 'active' || r.state === 'probing' || r.state === 'pausing') {
        this.updateRecord(r.id, {
          state: 'recoverableError',
          lastError: makeError('WORKER_CRASHED'),
        });
      }
    }
    this.emit({ type: 'worker-crashed' });
  }

  private send(cmd: DownloadCommand): void {
    this.downloadWorker?.postMessage(cmd);
  }

  // ───────────────── boot ─────────────────

  /** Restore the library. Never restarts the network on its own. PH-07 gate */
  async boot(): Promise<SessionRecord[]> {
    const stored = await loadSessions();
    let onDisk: Map<
      string,
      {
        manifestComplete: boolean;
        spans: ByteSpan[];
        size: number;
        hasManifest: boolean;
        identity: SessionRecord['identity'];
      }
    > | null = null;
    try {
      await this.ensureStarted();
      const list = await this.rpc!.call({ op: 'list' });
      onDisk = new Map(
        list.sessions.map((s) => [
          s.sessionId,
          {
            manifestComplete: s.manifest?.complete === true,
            spans: s.manifest?.spans ?? [],
            size: s.size,
            hasManifest: s.manifest !== null,
            identity: s.manifest?.identity ?? null,
          },
        ]),
      );
      // Orphans from a crash between creating storage and saving the record.
      const known = new Set(stored.map((r) => r.id));
      for (const s of list.sessions) {
        if (!known.has(s.sessionId) && (s.manifest === null || s.manifest.spans.length === 0)) {
          void this.rpc!.call({ op: 'remove', sessionId: s.sessionId }).catch(() => undefined);
        }
      }
    } catch {
      /* storage unavailable: records still list, flagged below */
    }

    const out: SessionRecord[] = [];
    for (const rec of stored) {
      let r = { ...rec };
      const disk = onDisk?.get(r.id);
      if (onDisk && !disk) {
        // The browser evicted our data (or the user cleared site data).
        r = {
          ...r,
          state: 'recoverableError',
          committedBytes: 0,
          lastError: makeError('STORAGE_CORRUPT', { evicted: true }),
        };
      } else if (disk) {
        const measured = IntervalSet.from(disk.spans).measure();
        r.committedBytes = measured;
        if (r.state === 'complete' && !disk.manifestComplete) {
          r = { ...r, state: 'paused', lastError: makeError('STORAGE_CORRUPT') };
        } else if (r.state !== 'complete') {
          if (disk.manifestComplete) r.state = 'complete';
          else if (['active', 'probing', 'pausing', 'queued'].includes(r.state)) r.state = 'paused';
        }
      } else if (['active', 'probing', 'pausing', 'queued'].includes(r.state)) {
        r.state = 'paused';
      }
      this.records.set(r.id, r);
      out.push(r);
      void saveSession(r);
      if (disk) this.seedHandle(r, disk.spans, disk.identity);
    }
    this.emit({ type: 'ready' });
    return out;
  }

  /**
   * Give a restored session a handle backed by its durable manifest, so saved
   * bytes can be read (and viewers can open) without any network. ARCH-019
   */
  private seedHandle(
    r: SessionRecord,
    spans: ByteSpan[],
    identity: SessionRecord['identity'],
  ): void {
    const handle = this.handles.get(r.id) ?? new SessionHandle(r.id, this);
    this.handles.set(r.id, handle);
    const id = identity ?? r.identity;
    const snapshot: SessionSnapshot = {
      sessionId: r.id,
      epoch: 0,
      state: r.state,
      identity: id,
      rangeProvenance: 'unknown',
      counters: {
        transferredBytes: 0,
        committedBytes: IntervalSet.from(spans).measure(),
        requests: 0,
        failedRequests: 0,
        retriedBytes: 0,
      },
      readable: spans,
      inFlight: [],
      bytesPerSecond: null,
      error: r.lastError,
      mode: r.mode,
      seekable: r.state === 'complete',
      exposedHeaders: {
        contentRange: false,
        etag: false,
        lastModified: false,
        contentLength: false,
      },
      contentType: r.contentType,
      contentDispositionName: null,
      finalHost: null,
    };
    handle.update(snapshot);
    this.emit({ type: 'snapshot', id: r.id, snapshot });
  }

  // ───────────────── lifecycle ─────────────────

  records_(): SessionRecord[] {
    return [...this.records.values()];
  }

  handle(id: string): SessionHandle | undefined {
    return this.handles.get(id);
  }

  getRecord(id: string): SessionRecord | undefined {
    return this.records.get(id);
  }

  /** Begin a brand-new download from a validated URL. */
  async startNew(parsed: ParsedSource): Promise<string> {
    await this.ensureStarted();
    const settings = this.getSettings();
    const id = crypto.randomUUID();
    const fp = await urlFingerprint(parsed.url);
    const kindGuess = kindFromExtension(parsed.extension);
    const keepFull = parsed.url.search === '' || settings.rememberSignedLinks;
    const now = Date.now();
    const record: SessionRecord = {
      id,
      createdAt: now,
      updatedAt: now,
      name: parsed.inferredName,
      host: parsed.host,
      safeUrl: parsed.safeDisplay,
      storedUrl: keepFull ? parsed.url.href : null,
      extension: parsed.extension,
      kind: kindGuess,
      container: null,
      contentType: null,
      state: 'queued',
      totalSize: null,
      committedBytes: 0,
      identity: null,
      mode: settings.mode,
      lastError: null,
      position: 0,
    };
    this.records.set(id, record);
    this.liveUrls.set(id, parsed.url.href);
    this.handles.set(id, new SessionHandle(id, this));
    await saveSession(record);
    this.emit({ type: 'record', record });

    try {
      await this.rpc!.call({ op: 'open', sessionId: id, create: true });
    } catch (e) {
      this.failStorage(id, e);
      return id;
    }

    const epoch = nextEpoch();
    this.epochs.set(id, epoch);
    this.send({
      type: 'init',
      sessionId: id,
      url: parsed.url.href,
      epoch,
      urlFingerprint: fp,
      mode: record.mode,
      maxConcurrency: defaultConcurrency(this.caps),
    });
    this.send({ type: 'start', sessionId: id, epoch });
    this.updateRecord(id, { state: 'probing' });
    return id;
  }

  /**
   * Continue a paused or interrupted session. The link may be required again
   * if it was not stored. The file identity is re-validated by the first response.
   */
  async resume(id: string, rawUrl?: string): Promise<void> {
    const record = this.records.get(id);
    if (!record) return;
    await this.ensureStarted();

    let href = record.storedUrl ?? this.liveUrls.get(id) ?? null;
    let fp = record.identity?.urlFingerprint ?? null;
    if (rawUrl !== undefined) {
      const parsed = parseSourceUrl(rawUrl);
      if (!parsed.ok) throw new OneFlowError(parsed.error.code);
      const newFp = await urlFingerprint(parsed.source.url);
      if (fp !== null && newFp !== fp)
        throw new OneFlowError('SOURCE_CHANGED', { reason: 'different link' });
      href = parsed.source.url.href;
      this.liveUrls.set(id, href);
      fp = newFp;
      const keep = parsed.source.url.search === '' || this.getSettings().rememberSignedLinks;
      this.updateRecord(id, { storedUrl: keep ? href : null });
    }
    if (href && !fp) fp = await urlFingerprint(new URL(href));
    if (!href || !fp) throw new NeedsUrl(id);

    let opened;
    try {
      opened = await this.rpc!.call({ op: 'open', sessionId: id, create: false });
    } catch (e) {
      this.failStorage(id, e);
      return;
    }
    const manifest = opened.manifest;
    const identity = manifest?.identity ?? record.identity;
    if (!this.handles.has(id)) this.handles.set(id, new SessionHandle(id, this));
    this.sniffed.delete(id);

    const epoch = nextEpoch();
    this.epochs.set(id, epoch);
    this.send({
      type: 'init',
      sessionId: id,
      url: href,
      epoch,
      urlFingerprint: fp,
      mode: record.mode,
      maxConcurrency: defaultConcurrency(this.caps),
      ...(identity ? { resume: { identity, committed: manifest?.spans ?? [] } } : {}),
    });
    this.send({ type: 'start', sessionId: id, epoch });
    this.updateRecord(id, { state: identity ? 'active' : 'probing', lastError: null });
  }

  pause(id: string): void {
    this.send({ type: 'pause', sessionId: id });
    this.updateRecord(id, { state: 'pausing' });
  }

  setMode(id: string, mode: DownloadMode): void {
    this.send({ type: 'setMode', sessionId: id, mode });
    this.updateRecord(id, { mode });
  }

  /** The user chose to discard a changed or damaged partial copy and start over. */
  async restartFresh(id: string, rawUrl?: string): Promise<void> {
    const rec = this.records.get(id);
    if (!rec) return;
    this.send({ type: 'dispose', sessionId: id });
    await this.rpc?.call({ op: 'remove', sessionId: id }).catch(() => undefined);
    await this.ensureStarted();
    await this.rpc!.call({ op: 'open', sessionId: id, create: true });
    this.updateRecord(id, {
      identity: null,
      totalSize: null,
      committedBytes: 0,
      state: 'queued',
      lastError: null,
      kind: kindFromExtension(rec.extension),
      container: null,
    });
    this.sniffed.delete(id);
    const href = rawUrl ?? rec.storedUrl ?? this.liveUrls.get(id) ?? null;
    if (!href) throw new NeedsUrl(id);
    const parsed = parseSourceUrl(href);
    if (!parsed.ok) throw new OneFlowError(parsed.error.code);
    const fp = await urlFingerprint(parsed.source.url);
    const epoch = nextEpoch();
    this.epochs.set(id, epoch);
    this.send({
      type: 'init',
      sessionId: id,
      url: parsed.source.url.href,
      epoch,
      urlFingerprint: fp,
      mode: rec.mode,
      maxConcurrency: defaultConcurrency(this.caps),
    });
    this.send({ type: 'start', sessionId: id, epoch });
    this.updateRecord(id, {
      state: 'probing',
      storedUrl:
        parsed.source.url.search === '' || this.getSettings().rememberSignedLinks
          ? parsed.source.url.href
          : null,
    });
  }

  /** One path for deletion: stop requests, close handles, revoke URLs, delete bytes and metadata. ARCH-018 */
  async remove(id: string): Promise<void> {
    this.send({ type: 'dispose', sessionId: id });
    this.handles.get(id)?.dispose();
    this.handles.delete(id);
    revokeAllFor(id);
    try {
      await this.ensureStarted();
      await this.rpc!.call({ op: 'remove', sessionId: id });
    } catch {
      /* nothing on disk to remove */
    }
    this.records.delete(id);
    this.liveUrls.delete(id);
    this.epochs.delete(id);
    this.sniffed.delete(id);
    await deleteSessionRecord(id);
    this.emit({ type: 'removed', id });
  }

  // ───────────────── bytes ─────────────────

  sendDemand(id: string, demand: Demand): void {
    this.send({ type: 'demand', sessionId: id, demand });
  }

  cancelDemands(id: string, belowEpoch: number): void {
    this.send({ type: 'cancelDemands', sessionId: id, belowEpoch });
  }

  async readStorage(id: string, offset: number, length: number): Promise<ArrayBuffer> {
    await this.ensureStarted();
    const r = await this.rpc!.call({ op: 'read', sessionId: id, offset, length });
    return r.data;
  }

  /**
   * File-backed access to a COMPLETE file for native media elements and export.
   * Releases the exclusive handle so the browser can read the file lazily
   * from disk without copying it into memory. ARCH-009, ADR-015
   */
  async getCompleteFile(id: string): Promise<File> {
    const rec = this.records.get(id);
    if (!rec || rec.state !== 'complete')
      throw new OneFlowError('PREVIEW_FAILED', { reason: 'incomplete' });
    await this.ensureStarted();
    await this.rpc!.call({ op: 'release', sessionId: id });
    const root = await navigator.storage.getDirectory();
    const app = await root.getDirectoryHandle('oneflow-v1');
    const dir = await app.getDirectoryHandle(id);
    const fh = await dir.getFileHandle('data.bin');
    return fh.getFile();
  }

  async storageEstimate(): Promise<{
    usage: number | null;
    quota: number | null;
    persisted: boolean | null;
  }> {
    try {
      await this.ensureStarted();
      const r = await this.rpc!.call({ op: 'estimate' });
      return { usage: r.usage, quota: r.quota, persisted: r.persisted };
    } catch {
      return { usage: null, quota: null, persisted: null };
    }
  }

  async requestPersistence(): Promise<boolean> {
    try {
      await this.ensureStarted();
      return (await this.rpc!.call({ op: 'persist' })).granted;
    } catch {
      return false;
    }
  }

  // ───────────────── state sync ─────────────────

  private onSnapshot(s: SessionSnapshot): void {
    const rec = this.records.get(s.sessionId);
    if (!rec) return;
    const known = this.epochs.get(s.sessionId);
    // Late messages from a previous controller epoch must not overwrite newer state. ARCH-016
    if (known !== undefined && s.epoch < known) return;

    const handle = this.handles.get(s.sessionId);
    handle?.update(s);

    const patch: Partial<SessionRecord> = {
      state: s.state,
      committedBytes: s.counters.committedBytes,
      lastError: s.error,
    };
    if (s.identity) {
      patch.identity = s.identity;
      patch.totalSize = s.identity.totalSize;
    }
    if (s.contentType) patch.contentType = s.contentType;
    if (s.contentDispositionName && rec.name === safeFilename(rec.name) && rec.name === rec.name) {
      if (rec.container === null && rec.committedBytes === 0) patch.name = s.contentDispositionName;
    }
    this.updateRecord(s.sessionId, patch, true);
    this.emit({ type: 'snapshot', id: s.sessionId, snapshot: s });
    void this.trySniff(s.sessionId, s);
  }

  /** Identify the file from its first bytes once they are saved. D01-F10 */
  private async trySniff(id: string, s: SessionSnapshot): Promise<void> {
    if (this.sniffed.has(id)) return;
    const total = s.identity?.totalSize ?? null;
    const need = total !== null ? Math.min(SNIFF_BYTES, total) : SNIFF_BYTES;
    if (need === 0) return;
    const readable = IntervalSet.from(s.readable);
    if (!readable.covers({ start: 0, end: need })) return;
    this.sniffed.add(id);
    const handle = this.handles.get(id);
    const rec = this.records.get(id);
    if (!handle || !rec) return;
    try {
      const r = await handle.readAt(0, need);
      if (r.status !== 'ok') {
        this.sniffed.delete(id);
        return;
      }
      const sn = sniffBytes(r.bytes);
      const kind: MediaKind = resolveKind(
        sn,
        kindFromMime(rec.contentType),
        kindFromExtension(rec.extension),
      );
      this.updateRecord(id, { kind, container: sn.container });
    } catch {
      this.sniffed.delete(id);
    }
  }

  private failStorage(id: string, e: unknown): void {
    const locked = e instanceof DOMException && e.name === 'NoModificationAllowedError';
    const quota = e instanceof DOMException && e.name === 'QuotaExceededError';
    const error: AppError = locked
      ? makeError('STORAGE_UNAVAILABLE', { locked: true })
      : quota
        ? makeError('QUOTA_EXCEEDED')
        : makeError('STORAGE_UNAVAILABLE');
    this.updateRecord(id, {
      state: error.recoverable ? 'recoverableError' : 'fatalError',
      lastError: error,
    });
  }

  private updateRecord(id: string, patch: Partial<SessionRecord>, throttle = false): void {
    const prev = this.records.get(id);
    if (!prev) return;
    const next: SessionRecord = { ...prev, ...patch, updatedAt: Date.now() };
    this.records.set(id, next);
    this.emit({ type: 'record', record: next });
    const last = this.lastPersist.get(id);
    const stateChanged = !last || last.state !== next.state;
    if (!throttle || stateChanged || next.updatedAt - (last?.at ?? 0) > 1500) {
      this.lastPersist.set(id, { at: next.updatedAt, state: next.state });
      void patchSession(id, { ...patch });
    }
  }

  /** Remember playback position for the library. */
  savePosition(id: string, seconds: number): void {
    const rec = this.records.get(id);
    if (!rec) return;
    this.records.set(id, { ...rec, position: seconds });
    void patchSession(id, { position: seconds });
  }
}
